import moment from 'moment';
import { Employee, IEmployee } from '../employee';
import { IUserData, User } from '../user';
import {
  IVacationCarryOverApproval,
  IVacationCarryOverApprovalData,
  IVacationCarryOverApprovalFieldError,
  IVacationCarryOverApprovalFieldErrorData,
  IVacationCarryOverRequest,
  IVacationCarryOverRequestComment,
  IVacationCarryOverRequestCommentData,
  IVacationCarryOverRequestCommentFieldError,
  IVacationCarryOverRequestCommentFieldErrorData,
  IVacationCarryOverRequestData,
  IVacationCarryOverRequestFieldError,
  IVacationCarryOverRequestFieldErrorData,
  IVacationCarryOverRequestStateInfoData,
  VacationCarryOverApprovalState,
  VacationCarryOverRequestStateEnum
} from './vacation-carry-over-request.types';
import {
  AbstractAttachment,
  IAttachment,
  IAttachmentData
} from '../attachment';
import {
  AbstractMentionAttachmentComment,
  IMentionAttachmentComment,
  IMentionAttachmentCommentData
} from '../comment';

export class VacationCarryOverRequest implements IVacationCarryOverRequest {
  readonly id?: number;
  readonly state: VacationCarryOverRequestStateEnum;
  /** Can be optional in backed if user is creating on their own behalf */
  readonly employee?: IEmployee | number;
  readonly amount_requested: number;
  readonly year: number;
  readonly reason: string;
  readonly approvers: VacationCarryOverApproval[];
  readonly created_at: Date;
  readonly updated_at: Date;

  constructor(data: IVacationCarryOverRequestData) {
    if (data.id) {
      this.id = data.id;
    }

    this.state = data.state ?? VacationCarryOverRequestStateEnum.Draft;

    if (data.employee) {
      if (typeof data.employee == 'number') {
        this.employee = data.employee;
      } else {
        this.employee = new Employee(data.employee);
      }
    }

    this.year = data.year;
    this.reason = data.reason ?? '';
    this.amount_requested = data.amount_requested ?? 0;

    this.approvers = data.approvers.map(
      approver => new VacationCarryOverApproval(approver)
    );

    this.created_at = moment(data.created_at as string).toDate();

    this.updated_at = moment(data.updated_at as string).toDate();
  }

  getStateInfo(): IVacationCarryOverRequestStateInfoData {
    // Returns color based on state
    switch (this.state) {
      case VacationCarryOverRequestStateEnum.Draft:
        return { display_name: 'Draft', color: 'draft' };

      case VacationCarryOverRequestStateEnum.Submitted:
        return { display_name: 'Waiting for Approval', color: 'submitted' };

      case VacationCarryOverRequestStateEnum.Approved:
        return { display_name: 'Approved', color: 'approved' };

      case VacationCarryOverRequestStateEnum.Rejected:
        return { display_name: 'Rejected', color: 'reject' };

      case VacationCarryOverRequestStateEnum.Cancelled:
        return { display_name: 'Cancelled', color: 'cancel' };

      default:
        return { display_name: '', color: 'white' };
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class VacationCarryOverApproval implements IVacationCarryOverApproval {
  readonly id?: number | undefined;
  readonly approver: number | IUserData;
  readonly status: VacationCarryOverApprovalState;
  readonly created_at?: string | undefined;
  readonly updated_at?: string | undefined;

  constructor(data: IVacationCarryOverApprovalData) {
    if (data.id) {
      this.id = data.id;
    }

    if (typeof data.approver == 'number') {
      this.approver = data.approver;
    } else {
      this.approver = new User(data.approver);
    }

    this.status = data.status ?? VacationCarryOverApprovalState.Tentative;

    if (data.created_at) {
      this.created_at = data.created_at;
    }

    if (data.updated_at) {
      this.updated_at = data.updated_at;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class VacationCarryOverRequestCommentAttachment extends AbstractAttachment {
  constructor(data: IAttachmentData) {
    super(data);
  }

  toString(): string {
    return this.filename;
  }
}

export class VacationCarryOverRequestComment
  extends AbstractMentionAttachmentComment
  implements IMentionAttachmentComment {
  readonly text: string = '';
  readonly attachments: IAttachment[] = [];

  constructor(data?: IMentionAttachmentCommentData) {
    super(data);

    if (data) {
      this.text = data.text ?? '';

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment =>
            new VacationCarryOverRequestCommentAttachment(attachment)
        );
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  toString() {
    return this.text;
  }
}

export class VacationCarryOverRequestFieldError
  implements IVacationCarryOverRequestFieldError {
  readonly year: string[] = [];
  readonly amount_requested: string[] = [];
  readonly reason: string[] = [];
  readonly approvers: IVacationCarryOverApprovalFieldErrorData[] = [];
  readonly state: string[] = [];
  readonly employee: string[] = [];
  readonly owner: string[] = [];

  constructor(data?: IVacationCarryOverRequestFieldErrorData) {
    if (data) {
      if (data.state) {
        this.state = data.state;
      }
      if (data.employee) {
        this.employee = data.employee;
      }
      if (data.owner) {
        this.owner = data.owner;
      }
      if (data.year) {
        this.year = data.year;
      }
      if (data.amount_requested) {
        this.amount_requested = data.amount_requested;
      }
      if (data.reason) {
        this.reason = data.reason;
      }

      if (data.approvers) {
        this.approvers = data.approvers.map(
          approver => new VacationCarryOverApprovalFieldError(approver)
        );
      }
    }
  }

  clearErrors(): void {
    this.state.splice(0, this.state.length);
    this.employee.splice(0, this.employee.length);
    this.amount_requested.splice(0, this.amount_requested.length);
    this.year.splice(0, this.year.length);
    this.reason.splice(0, this.reason.length);
    this.approvers.splice(0, this.approvers.length);
  }

  static isVacationCarryOverRequestFieldError(
    data: IVacationCarryOverRequestFieldErrorData
  ): boolean {
    if (data.state && Array.isArray(data.state)) {
      return true;
    } else if (data.employee && Array.isArray(data.employee)) {
      return true;
    } else if (data.year && Array.isArray(data.year)) {
      return true;
    } else if (data.amount_requested && Array.isArray(data.amount_requested)) {
      return true;
    } else if (data.reason && Array.isArray(data.reason)) {
      return true;
    } else if (data.approvers && Array.isArray(data.approvers)) {
      return true;
    }
    return false;
  }
}

export class VacationCarryOverApprovalFieldError
  implements IVacationCarryOverApprovalFieldError {
  readonly approver: string[] = [];
  readonly status: string[] = [];

  constructor(data?: IVacationCarryOverApprovalFieldErrorData) {
    if (data) {
      if (data.approver) {
        this.approver = data.approver;
      }
      if (data.status) {
        this.status = data.status;
      }
    }
  }

  clearErrors(): void {
    this.approver.splice(0, this.approver.length);
    this.status.splice(0, this.status.length);
  }

  static isVacationCarryOverApprovalFieldError(
    data: IVacationCarryOverApprovalFieldErrorData
  ): boolean {
    if (data.approver && Array.isArray(data.approver)) {
      return true;
    }
    if (data.status && Array.isArray(data.status)) {
      return true;
    }
    return false;
  }
}

export class VacationCarryOverRequestCommentFieldError
  implements IVacationCarryOverRequestCommentFieldError {
  readonly text: string[] = [];

  constructor(data?: IVacationCarryOverRequestCommentFieldErrorData) {
    if (data) {
      if (data.text) {
        this.text = data.text;
      }
    }
  }

  clearErrors(): void {
    this.text.splice(0, this.text.length);
  }

  static isVacationCarryOverRequestCommentFieldError(
    data: IVacationCarryOverRequestCommentFieldErrorData
  ): boolean {
    if (data.text && Array.isArray(data.text)) {
      return true;
    }
    return false;
  }
}
